import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAppContext } from "../AppProviders";
import { API_URL } from "../api/helpers";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { ChevronLeftIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import { Document, Page } from "react-pdf";
import { Modal } from "../shared-components/Modal";
import {
  useCompleteThreeSixtyReview,
  useCompleteSecondaryQA,
  useComplete360Amend,
  useGetSubworkflowData,
  useUnlockWorkflow,
} from "../api/WorkflowApi";
import { useScrollToBottom } from "./useScrollToBottom";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import { useSummary360Data } from "./summary/useSummary360Data";
import { useReportByUUID } from "../api/ReportsApi";
import { WORKFLOW_STEP_STATUS_ENUM, WorkflowRun } from "../api/Workflows";
import { ReportSummaryItemApi } from "../api/Reports";
import { get360SubworkflowRunUuid } from "./reportAssignment";

const Review360: React.FC = () => {
  const { data: reportDataLoaded, mutate: reportDataGet } = useReportByUUID(undefined, true);
  const { data: subworkflowDataLoaded, mutate: subworkflowDataGet } =
    useGetSubworkflowData();

  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { requestApi } = useAppContext();
  const isQAMode = location.pathname.includes("/qa-review");
  const { canEdit360Reports, canEdit360Qa, canNavAmendment } =
    usePermissionValidator();
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const requestApiRef = useRef(requestApi);
  const blobUrlRef = useRef<string | null>(null);
  const hasFetchedForIdRef = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [showSendToQAModal, setShowSendToQAModal] = useState<boolean>(false);
  const [showCompleteReviewModal, setShowCompleteReviewModal] =
    useState<boolean>(false);
  const [showCompleteSecondaryQAModal, setShowCompleteSecondaryQAModal] =
    useState<boolean>(false);
  const [hideActions, setHideActions] = useState<boolean>(true);

  const [summaryNeedsReview, setSummaryNeedsReview] = useState<boolean>(true);
  const { summaryItems, summaryItemsLoading } = useSummary360Data(params.id);

  const locationState = location.state as
    | {
        reportData: ReportSummaryItemApi | undefined;
        subworkflowData: WorkflowRun | undefined;
        amendment360?: boolean;
      }
    | undefined;
  const reportData = locationState?.reportData ?? reportDataLoaded;
  const isAmendment360 =
    locationState?.amendment360 === true ||
    reportData?.step_name === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyAmend;

  useEffect(() => {
    if (!locationState?.reportData) {
      reportDataGet(params.id as string);
    }
  }, [locationState?.reportData, reportDataGet, params.id]);

  useEffect(() => {
    if (!locationState?.subworkflowData && reportData) {
      subworkflowDataGet(reportData.workflow_run_uuid);
    }
  }, [locationState?.subworkflowData, reportData, subworkflowDataGet]);

  useEffect(() => {
    if (subworkflowDataLoaded || locationState?.subworkflowData) {
      const subworkflowData = subworkflowDataLoaded
        ? subworkflowDataLoaded
        : locationState?.subworkflowData;
      if (subworkflowData) {
        const matches: WorkflowRun[] = Array.isArray(subworkflowData)
          ? subworkflowData.filter((wr) => wr.name === "360")
          : [subworkflowData];
        if (matches && matches.length > 0 && matches[0].external_id) {
          const lastRunningStep = matches[0].last_running_step;
          setHideActions(
            lastRunningStep === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyWebSearch
          );
        }
      }
    }
  }, [subworkflowDataLoaded, locationState?.subworkflowData]);

  useEffect(() => {
    if (summaryItemsLoading || !summaryItems) {
      return;
    }
    const keyPhrases = [
      "needs review",
      "need review",
      "requires review",
      "require review",
    ];
    if (
      keyPhrases.some((phrase) =>
        summaryItems.some((item) => item.body.toLowerCase().includes(phrase))
      )
    ) {
      setSummaryNeedsReview(true);
    } else {
      setSummaryNeedsReview(false);
    }
  }, [summaryItems, summaryItemsLoading, setSummaryNeedsReview]);

  const { mutateAsync: completeReview, isLoading: isCompletingReview } =
    useCompleteThreeSixtyReview();
  const {
    mutateAsync: completeSecondaryQA,
    isLoading: isCompletingSecondaryQA,
  } = useCompleteSecondaryQA();
  const { mutateAsync: complete360Amend, isLoading: isCompletingAmendment360 } =
    useComplete360Amend();
  const { mutateAsync: unlockWorkflow } = useUnlockWorkflow();

  const subworkflowData =
    subworkflowDataLoaded ?? locationState?.subworkflowData;
  const workflowRun360Uuid = useMemo(() => {
    if (!subworkflowData) return undefined;
    const list = Array.isArray(subworkflowData)
      ? subworkflowData
      : [subworkflowData];
    return get360SubworkflowRunUuid(list);
  }, [subworkflowData]);

  // Use scroll detection hook
  const { isScrolledToBottom, handleScroll } = useScrollToBottom({
    containerRef,
    contentReady: numPages,
    resetKey: pdfUrl,
  });

  // keep a stable ref to requestApi to avoid effect re-runs
  useEffect(() => {
    requestApiRef.current = requestApi;
  }, [requestApi]);

  // Cleanup blob URL and abort in-flight requests on unmount
  useEffect(() => {
    return () => {
      if (blobUrlRef.current && blobUrlRef.current.startsWith("blob:")) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
      // Abort any in-flight requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    const id = params.id as string | undefined;
    if (!id) {
      return;
    }

    // Abort any previous in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create a new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Reset state for new report
    hasFetchedForIdRef.current = null;
    setIsLoading(true);
    setError("");
    setNumPages(0);

    // Get the PDF URL for inline display
    const fetchPdfUrl = async () => {
      try {
        const response = await requestApiRef.current.get(
          `${API_URL}/reports/pdf/${id}`
        );

        // Check if this request was aborted or if params.id has changed
        if (abortController.signal.aborted || params.id !== id) {
          return;
        }

        if (response.data && response.data.report_url) {
          // Fetch the PDF as a blob to prevent download
          const pdfResponse = await fetch(response.data.report_url, {
            signal: abortController.signal,
          });

          // Check again if request was aborted after fetch
          if (abortController.signal.aborted || params.id !== id) {
            return;
          }

          const blob = await pdfResponse.blob();

          // Final check before updating state
          if (abortController.signal.aborted || params.id !== id) {
            return;
          }

          // Revoke any previous blob URL before setting a new one
          if (blobUrlRef.current && blobUrlRef.current.startsWith("blob:")) {
            URL.revokeObjectURL(blobUrlRef.current);
          }

          const newBlobUrl = URL.createObjectURL(blob);
          blobUrlRef.current = newBlobUrl;
          hasFetchedForIdRef.current = id;
          setPdfUrl(newBlobUrl);
        } else {
          if (abortController.signal.aborted || params.id !== id) {
            return;
          }
          setError("No PDF URL found");
        }
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          return;
        }

        if (!abortController.signal.aborted && params.id === id) {
          console.error("Error fetching PDF:", err);
          setError("Failed to load PDF");
        }
      } finally {
        // Only update loading state if this request is still current
        if (!abortController.signal.aborted && params.id === id) {
          setIsLoading(false);
        }
      }
    };

    fetchPdfUrl();

    return () => {
      abortController.abort();
    };
  }, [params.id]);

  const handleSendToQAClick = useCallback(() => {
    if (!isScrolledToBottom) return;
    setShowSendToQAModal(true);
  }, [isScrolledToBottom]);

  const handleCompleteReviewClick = useCallback(() => {
    if (!isScrolledToBottom) return;
    setShowCompleteReviewModal(true);
  }, [isScrolledToBottom]);

  const handleSendToQAConfirm = useCallback(async () => {
    const reportId = params.id as string | undefined;
    if (!reportId) {
      setShowSendToQAModal(false);
      return;
    }

    // Prevent concurrent mutations
    if (isCompletingReview) {
      setShowSendToQAModal(false);
      return;
    }

    try {
      await completeReview({
        report_uuid: reportId,
        skip_three_sixty_qa: false,
      });
      if (workflowRun360Uuid) {
        try {
          await unlockWorkflow({
            workflowRunUUID: workflowRun360Uuid,
            success: true,
            reason: "step_done",
          });
        } catch (unlockError) {
          console.error(
            "Failed to unlock workflow after send to QA:",
            unlockError
          );
        }
      }
      setShowSendToQAModal(false);
      navigate("/360/investigating");
    } catch (error) {
      // Not showing error feedback to the user at the moment.
      setShowSendToQAModal(false);
      console.error("Failed to send to QA:", error);
    }
  }, [
    params.id,
    completeReview,
    isCompletingReview,
    navigate,
    workflowRun360Uuid,
    unlockWorkflow,
  ]);

  const handleCompleteReviewConfirm = useCallback(async () => {
    const reportId = params.id as string | undefined;
    if (!reportId) {
      setShowCompleteReviewModal(false);
      return;
    }

    if (isAmendment360) {
      if (isCompletingAmendment360) {
        setShowCompleteReviewModal(false);
        return;
      }
      try {
        await complete360Amend({ report_uuid: reportId });
        if (workflowRun360Uuid) {
          try {
            await unlockWorkflow({
              workflowRunUUID: workflowRun360Uuid,
              success: true,
              reason: "step_done",
            });
          } catch (unlockError) {
            console.error(
              "Failed to unlock workflow after complete 360 amend:",
              unlockError
            );
          }
        }
        setShowCompleteReviewModal(false);
        navigate("/reports/amendment", { state: { stepDone: true } });
      } catch (error) {
        setShowCompleteReviewModal(false);
        console.error("Failed to complete 360 amend step:", error);
      }
      return;
    }

    if (isCompletingReview) {
      setShowCompleteReviewModal(false);
      return;
    }

    try {
      await completeReview({
        report_uuid: reportId,
        skip_three_sixty_qa: true,
      });
      if (workflowRun360Uuid) {
        try {
          await unlockWorkflow({
            workflowRunUUID: workflowRun360Uuid,
            success: true,
            reason: "step_done",
          });
        } catch (unlockError) {
          console.error(
            "Failed to unlock workflow after complete review:",
            unlockError
          );
        }
      }
      setShowCompleteReviewModal(false);
      navigate("/360/investigating");
    } catch (error) {
      // Not showing error feedback to the user at the moment.
      setShowCompleteReviewModal(false);
      console.error("Failed to complete review:", error);
    }
  }, [
    params.id,
    completeReview,
    isCompletingReview,
    isAmendment360,
    isCompletingAmendment360,
    complete360Amend,
    navigate,
    workflowRun360Uuid,
    unlockWorkflow,
  ]);

  const handleCompleteSecondaryQAClick = useCallback(() => {
    if (!isScrolledToBottom) return;
    setShowCompleteSecondaryQAModal(true);
  }, [isScrolledToBottom]);

  const handleCompleteSecondaryQAConfirm = useCallback(async () => {
    const reportId = params.id as string | undefined;
    if (!reportId) {
      setShowCompleteSecondaryQAModal(false);
      return;
    }

    if (isCompletingSecondaryQA) {
      setShowCompleteSecondaryQAModal(false);
      return;
    }

    try {
      await completeSecondaryQA({ report_uuid: reportId });
      if (workflowRun360Uuid) {
        try {
          await unlockWorkflow({
            workflowRunUUID: workflowRun360Uuid,
            success: true,
            reason: "step_done",
          });
        } catch (unlockError) {
          console.error(
            "Failed to unlock workflow after complete secondary QA:",
            unlockError
          );
        }
      }
      setShowCompleteSecondaryQAModal(false);
      navigate("/360/qa");
    } catch (error) {
      setShowCompleteSecondaryQAModal(false);
      console.error("Failed to complete secondary QA:", error);
    }
  }, [
    params.id,
    completeSecondaryQA,
    isCompletingSecondaryQA,
    navigate,
    workflowRun360Uuid,
    unlockWorkflow,
  ]);

  const handleBack = useCallback(() => {
    if (isAmendment360) {
      const reportId = params.id;
      if (reportId) {
        navigate(`/360/${reportId}/workbench`, {
          state: { amendment360: true },
        });
      } else {
        navigate("/reports/amendment");
      }
      return;
    }
    if (isQAMode) {
      // In QA mode, go back to QA workbench
      const reportId = params.id;
      if (reportId) {
        navigate(`/360/${reportId}/qa-workbench`);
      } else {
        navigate("/360/qa");
      }
    } else if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/reports");
    }
  }, [navigate, isQAMode, isAmendment360, params.id]);

  const handleLoadSuccess = useCallback((data: { numPages: number }) => {
    setNumPages(data.numPages);
  }, []);

  // Intercept PDF link clicks to open in new tab
  useEffect(() => {
    const container = containerRef.current;
    if (!container || numPages === 0) return;

    const handleLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest("a");
      if (link && link.href && !link.href.startsWith("blob:")) {
        e.preventDefault();
        e.stopPropagation();
        window.open(link.href, "_blank", "noopener,noreferrer");
      }
    };

    container.addEventListener("click", handleLinkClick, true);
    return () => {
      container.removeEventListener("click", handleLinkClick, true);
    };
  }, [numPages]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-4">
            Error Loading PDF
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={handleBack}
            className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <ChevronLeftIcon className="w-4 h-4 mr-2" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <button
              onClick={handleBack}
              className="inline-flex items-center px-3 py-2 text-gray-600 hover:text-gray-900 mb-2"
            >
              <ChevronLeftIcon className="w-5 h-5 mr-2" />
              {isQAMode ? "Back to QA Workbench" : "Back to Workbench"}
            </button>
            <h1 className="text-2xl font-bold text-gray-900 px-3">
              {isQAMode ? "360 Review - Secondary QA" : "360 Review"}
            </h1>
          </div>
          <div className="flex items-center">
            {pdfUrl && (
              <a
                href={`${pdfUrl}#zoom=80`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-1 text-sm border bg-white text-gray-600 rounded hover:bg-gray-50"
              >
                Open in New Tab
              </a>
            )}
          </div>
        </div>
      </div>

      {/* PDF Container */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full bg-white">
          {pdfUrl ? (
            <div
              ref={containerRef}
              onScroll={handleScroll}
              className="h-full w-full overflow-y-auto"
            >
              <Document
                file={pdfUrl}
                onLoadSuccess={handleLoadSuccess}
                onLoadError={(error: Error) => {
                  console.error("Error loading PDF:", error);
                  setError("Failed to load PDF");
                }}
                loading={
                  <div className="flex items-center justify-center h-full">
                    <LoadingSpinner />
                  </div>
                }
                className="flex flex-col items-center bg-gray-800"
              >
                {Array.from({ length: numPages }, (_, i) => (
                  <Page
                    key={`page_${i + 1}`}
                    pageNumber={i + 1}
                    width={800}
                    className="mb-4"
                  />
                ))}
              </Document>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <p className="text-gray-500">No PDF available</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons - always show in 360 Amend mode; otherwise when not hiding (e.g. not in WebSearch step) */}
      {(!hideActions || isAmendment360) && (
        <div className="bg-white border-t px-6 py-4">
          {isAmendment360
            ? // 360 Amend mode - show only Complete Review (advances amendment workflow)
              canNavAmendment && (
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={handleCompleteReviewClick}
                    disabled={!isScrolledToBottom}
                    className={`inline-flex items-center px-6 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                      !isScrolledToBottom
                        ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                        : "bg-green-600 text-white hover:bg-green-700 focus:ring-green-500"
                    }`}
                  >
                    Complete Review
                  </button>
                </div>
              )
            : isQAMode
            ? // Secondary QA mode - show only Complete Review button
              canEdit360Qa && (
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={handleCompleteSecondaryQAClick}
                    disabled={!isScrolledToBottom}
                    className={`inline-flex items-center px-6 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                      !isScrolledToBottom
                        ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                        : "bg-green-600 text-white hover:bg-green-700 focus:ring-green-500"
                    }`}
                  >
                    Complete Review
                  </button>
                </div>
              )
            : // Investigator mode - show both buttons
              canEdit360Reports && (
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={handleSendToQAClick}
                    disabled={!isScrolledToBottom}
                    className={`inline-flex items-center px-6 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                      !isScrolledToBottom
                        ? "bg-orange-300 text-white cursor-not-allowed"
                        : "bg-orange-500 text-white"
                    }`}
                  >
                    Send to QA
                  </button>
                  {!summaryNeedsReview && (
                    <button
                      onClick={handleCompleteReviewClick}
                      disabled={!isScrolledToBottom}
                      className={`inline-flex items-center px-6 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                        !isScrolledToBottom
                          ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                          : "bg-green-600 text-white hover:bg-green-700 focus:ring-green-500"
                      }`}
                    >
                      Complete Review
                    </button>
                  )}
                </div>
              )}
        </div>
      )}

      {/* Send to QA Modal */}
      <Modal
        open={showSendToQAModal}
        onClose={() => setShowSendToQAModal(false)}
        title="Send to QA?"
        titleSize="text-2xl"
        icon={
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
            <CheckCircleIcon className="w-6 h-6 text-orange-600" />
          </div>
        }
      >
        <div className="mt-4">
          <p className="text-sm text-gray-500 mb-6 ml-14">
            Are you sure you want to send this to QA for review? Once submitted,
            any further edits will need to go through the QA process.
          </p>
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => setShowSendToQAModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={handleSendToQAConfirm}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 w-24"
            >
              OK
            </button>
          </div>
        </div>
      </Modal>

      {/* Complete Review Modal */}
      <Modal
        open={showCompleteReviewModal}
        onClose={() => setShowCompleteReviewModal(false)}
        title="Complete Review?"
        titleSize="text-2xl"
        icon={
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircleIcon className="w-6 h-6 text-green-600" />
          </div>
        }
      >
        <div className="mt-4">
          <p className="text-sm text-gray-600 mb-6 ml-14">
            Are you sure you want to complete this review? Once submitted,
            further changes may not be possible.
          </p>
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => setShowCompleteReviewModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={handleCompleteReviewConfirm}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              Continue
            </button>
          </div>
        </div>
      </Modal>

      {/* Complete Secondary QA Modal */}
      <Modal
        open={showCompleteSecondaryQAModal}
        onClose={() => setShowCompleteSecondaryQAModal(false)}
        title="Complete Review?"
        titleSize="text-2xl"
        icon={
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircleIcon className="w-6 h-6 text-green-600" />
          </div>
        }
      >
        <div className="mt-4">
          <p className="text-sm text-gray-600 mb-6 ml-14">
            Are you sure you want to complete this Secondary QA review? Once
            submitted, the report will be finalized.
          </p>
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => setShowCompleteSecondaryQAModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={handleCompleteSecondaryQAConfirm}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              Continue
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Review360;
